/* eslint-disable camelcase */
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";

import { useTranslation } from "react-i18next";

import { IdType, SortingRule } from "react-table";
import { HvButton, HvGrid } from "@hitachivantara/uikit-react-core";
import {
  HvCellProps,
  HvTableColumnConfig,
  useHvTable,
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvSortBy,
} from "@hitachivantara/uikit-react-lab";
import { Archive, Edit } from "@hitachivantara/uikit-react-icons";

import SimpleTable, {
  numberColumn,
  textColumn,
  actionsColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from "components/common/SimpleTable";

import {
  Category,
  useCategories,
  addCategory,
  editCategory,
  archiveCategory,
} from "lib/api";

import { NotificationContext } from "lib/context/NotificationContext";
import { useSnackbar } from "lib/provider/SnackbarProvider";
import ArchiveDialog from "components/common/ArchiveDialog";

import CategoryControls from "../CategoryControls";
import { CategoryFormData, CategoryFormMode } from "../AddEditCategoryDialog";

interface CategoryDataRow extends Record<string, unknown> {
  cat_desc: string;
  cat_name: string;
  create_timestamp: string;
  created_by: string;
  id: string;
  incident_type_count: number;
  pk_cat_id: number;
  update_by: string;
  update_timestamp: string;
  version: number;
}

const DEFAULT_SORT = [
  {
    id: "cat_name",
    desc: false,
  },
];

const sortFieldMap = {
  cat_name: "cat_name",
};

const translationScope = "incidentTypeCategory";

const CategoryTable: React.FC = () => {
  const { t } = useTranslation(translationScope);

  const [editing, setEditing] = useState<Category | "new" | "none">("none");
  const [tableData, setTableData] = useState<Category[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const onSave = useCallback(
    (data: CategoryFormData) => {
      if (editing !== "none") {
        if (editing !== "new") {
          return editCategory({
            ...editing,
            cat_name: data.name,
            cat_desc: data.description,
          });
        }

        return addCategory({
          cat_name: data.name,
          cat_desc: data.description,
        });
      }

      return Promise.resolve();
    },
    [editing]
  );

  const addButtonCallback = useCallback(() => {
    setEditing("new");
  }, []);

  const editButtonCallback = useCallback((category: Category) => {
    setEditing(category);
  }, []);

  const addEditDialogCloseCallback = () => {
    setEditing("none");
  };

  let editingMode: CategoryFormMode | undefined;
  let editingFormData;
  if (editing !== "none") {
    if (editing !== "new") {
      editingMode = "edit";
      editingFormData = {
        name: editing.cat_name,
        description: editing.cat_desc,
      };
    } else {
      editingMode = "add";
      editingFormData = { name: "", description: "" };
    }
  }

  const columns = useMemo(
    (): HvTableColumnConfig<Category>[] => [
      textColumn({
        Header: t("columns.categoryName") as string,
        accessor: "cat_name",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.categoryDescription") as string,
        accessor: "cat_desc",
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.categoryIncidentTypeCount") as string,
        accessor: "incident_type_count",
        style: { width: 150 },
        disableSortBy: true,
      }),
      actionsColumn({
        id: "edit",
        Cell: ({ row }: HvCellProps<Category>) => {
          return (
            <HvButton
              aria-label={t("labels.editCategoryButton")}
              icon
              onClick={() => editButtonCallback(row.original as Category)}
            >
              <Edit />
            </HvButton>
          );
        },
      }),
    ],
    [editButtonCallback, t]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

  const [sortBy, setSortBy] =
    useState<SortingRule<CategoryDataRow>[]>(DEFAULT_SORT);
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<CategoryDataRow>, boolean>
  >({});

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const { data, error, mutate } = useCategories(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: sortField ? encodeURIComponent(sortField) : undefined,
      Order: encodeURIComponent(sortBy[0].desc ? "desc" : "asc"),
      SearchKey: searchKey,
    },
    { suspense: tableData.length === 0 }
  );

  useEffect(() => {
    if (data) {
      setTableData(data.data);
      setTotalCount(data.totalCount);
    }
  }, [data]);

  const initialState = useMemo(() => {
    const entries = tableData.map<[string, boolean]>((v) => [
      v.pk_cat_id.toString(),
      v.incident_type_count > 0,
    ]);

    return {
      pageIndex: page,
      pageSize: size,
      lockedSelectionRowIds: (entries && Object.fromEntries(entries)) ?? {},
      selectedRowIds: selectedIds,
      sortBy,
      searchKey,
    };
  }, [tableData, page, size, selectedIds, sortBy, searchKey]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectCategoryCheckbox"),
    }),
    [t]
  );

  const bulkActions = useMemo(
    () => [
      {
        id: "archive",
        label: t("labels.bulkArchiveCategoryButton"),
        icon: <Archive />,
        onClick: () => setIsArchiveDialogOpen(true),
      },
    ],
    [t]
  );

  const instance = useHvTable<Category>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: Category): string => `${v.pk_cat_id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { pageIndex, pageSize, sortBy: sortByFromTable, selectedRowIds },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => setSelectedIds(selectedRowIds), [selectedRowIds]);

  const onEnterHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onArchiveClose = useCallback(
    (result, message, ids) => {
      setIsArchiveDialogOpen(false);

      if (ids) {
        const newSelectedIds = {
          ...selectedIds,
        };

        ids.forEach((id: string) => {
          delete newSelectedIds[id];
        });
        setSelectedIds(newSelectedIds);
      }

      if (result && ids) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setPage(DEFAULT_PAGE);
        mutate();
      } else if (message) {
        setBannerMessage(message);
        setShowBanner(true);
        mutate();
      }
    },
    [enqueueSnackbar, mutate, selectedIds, setBannerMessage, setShowBanner]
  );

  return (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <CategoryControls
          type="category"
          onAdd={addButtonCallback}
          onSave={onSave}
          onShouldRevalidate={mutate}
          onAddEditDialogClose={addEditDialogCloseCallback}
          mode={editingMode}
          editingFormData={editingFormData}
          onEnterHandler={onEnterHandler}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <ArchiveDialog
          scope={translationScope}
          isOpen={isArchiveDialogOpen}
          promise={archiveCategory}
          onClose={onArchiveClose}
          ids={Object.keys(selectedRowIds)}
        />
        <SimpleTable
          instance={instance}
          bulkActions={bulkActions}
          totalCount={totalCount}
          loading={(!data && !error) || tableData === []}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default CategoryTable;
